import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, ArrowUpRight, X, FileText, BookOpen } from "lucide-react";

const CATALOGUE_META = {
  cover: "https://images.unsplash.com/photo-1483691278019-cb7253bee49f?w=600&q=80&auto=format&fit=crop",
  pages: 48,
  version: "v2026.1",
  company: "Stenzia Displays LLP",
  url: "/stenzia-catalogue.pdf",
};

/**
 * Catalogue preview card → opens a small modal showing cover, page count, version
 * and a single "Download Catalogue" CTA pointing to the master company catalogue.
 */
export default function CataloguePreview({ buttonLabel = "Preview & Download Catalogue", testId = "catalogue-preview-trigger" }) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={testId}
        className="group inline-flex items-center gap-2.5 px-7 py-4 rounded-full bg-charcoal text-white text-sm font-medium hover:bg-stenzia-green transition-colors shadow-[0_10px_24px_-12px_rgba(11,107,83,0.45)]"
      >
        <BookOpen size={16} />
        {buttonLabel}
        <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}
            onClick={() => setOpen(false)}
            data-testid="catalogue-modal"
            className="fixed inset-0 z-[110] bg-charcoal/85 backdrop-blur-md flex items-end md:items-center justify-center p-0 md:p-8"
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full md:max-w-2xl bg-white rounded-t-3xl md:rounded-3xl shadow-2xl overflow-hidden"
            >
              <div className="flex items-start justify-between gap-4 px-6 md:px-8 py-5 border-b border-charcoal/10">
                <div>
                  <div className="overline text-stenzia-green">Company Catalogue</div>
                  <div className="mt-1 font-display text-xl md:text-2xl tracking-tight text-charcoal">Preview before download</div>
                </div>
                <button onClick={() => setOpen(false)} data-testid="catalogue-modal-close" className="w-10 h-10 rounded-full bg-[#F6F7F5] hover:bg-charcoal hover:text-white text-charcoal flex items-center justify-center transition-colors">
                  <X size={18} />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-5 gap-6 p-6 md:p-8">
                <div className="sm:col-span-2 relative rounded-2xl overflow-hidden aspect-[3/4] bg-charcoal shadow-lg">
                  <img src={CATALOGUE_META.cover} alt="STENZIA catalogue cover" draggable="false" className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-tr from-charcoal/60 via-transparent to-transparent" />
                  <div className="absolute bottom-3 left-3 text-white">
                    <div className="text-[10px] tracking-[0.22em] uppercase font-semibold opacity-80">{CATALOGUE_META.version}</div>
                    <div className="mt-0.5 font-display text-sm tracking-tight">STENZIA</div>
                  </div>
                </div>
                <div className="sm:col-span-3 flex flex-col">
                  <div className="font-display text-2xl tracking-tight text-charcoal">{CATALOGUE_META.company}</div>
                  <div className="mt-1 text-sm text-charcoal/60">Master Product Catalogue · {CATALOGUE_META.version}</div>
                  <dl className="mt-6 grid grid-cols-2 gap-4">
                    <div>
                      <dt className="text-[10px] uppercase tracking-[0.22em] text-charcoal/50 font-semibold">Pages</dt>
                      <dd className="mt-1 font-display text-lg text-charcoal">{CATALOGUE_META.pages}</dd>
                    </div>
                    <div>
                      <dt className="text-[10px] uppercase tracking-[0.22em] text-charcoal/50 font-semibold">Format</dt>
                      <dd className="mt-1 font-display text-lg text-charcoal flex items-center gap-1.5"><FileText size={15} /> PDF</dd>
                    </div>
                    <div>
                      <dt className="text-[10px] uppercase tracking-[0.22em] text-charcoal/50 font-semibold">Series</dt>
                      <dd className="mt-1 text-sm text-charcoal/75">Glide · Flip · Motion · 360 · Mockup</dd>
                    </div>
                    <div>
                      <dt className="text-[10px] uppercase tracking-[0.22em] text-charcoal/50 font-semibold">Updated</dt>
                      <dd className="mt-1 text-sm text-charcoal/75">2026</dd>
                    </div>
                  </dl>
                  <a
                    href={CATALOGUE_META.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="download-catalogue"
                    className="group mt-8 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-stenzia-green text-white text-sm font-medium hover:bg-charcoal transition-colors"
                  >
                    <Download size={15} /> Download Catalogue
                    <ArrowUpRight size={15} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
